import { useDispatch } from 'react-redux';
import { CheckboxItem } from 'components/core/checkbox-group';

import { useAppSelector } from 'redux/store';
import { setModalOpen, setModalTitle, setModalType } from 'redux/stores/permits';
import { PermitsOverviewFieldsFragment } from 'api/graphql-types';

import { IFilters } from 'components/filter-sidebar/filter-sidebar';
import { useLaw, useProduct } from '../queries';
import { useTheme } from 'utils/hooks/queries';

interface IPermitsFilterProps {
	module: PermitsOverviewFieldsFragment;
}

export const usePermitsFilters = (
	props: IPermitsFilterProps,
	onChange: (themeIds: string[], lawIds: string[], productType: string[]) => void
): IFilters => {
	const dispatch = useDispatch();
	const maxItemsAmount = 4;
	const { selectedThemes, setSelectedThemesParam } = useTheme();
	const { selectedLaws, setSelectedLawsParam } = useLaw();
	const { selectedProductTypes, setSelectedProductTypesParam } = useProduct();

	const { themes, totalThemes } = useAppSelector((state) => state.themes);
	const { permitLaws, permitLawCount, permitProductTypes, permitProductTypeCount } = useAppSelector(
		(state) => state.permits
	);

	const lawOptions = permitLaws && permitLaws.map(({ id, name }) => ({ id, label: name }));

	const productTypeOptions = permitProductTypes && permitProductTypes.map(({ id, name }) => ({ id, label: name }));

	const themeOptions = themes?.map((theme) => ({
		id: theme.documentId,
		label: theme?.attributes?.title
	})) as CheckboxItem[];

	const themesFilter = {
		maxItemsAmount,
		id: 'themes',
		type: 'checkbox',
		title: props.module.themeFilterText,
		onChange: (items: CheckboxItem[], active: boolean): void => {
			if (active) {
				const mergedThemes = [...selectedThemes, ...items];
				setSelectedThemesParam(mergedThemes.length > 0 ? mergedThemes : null);
				onChange(
					mergedThemes.map((item) => String(item.id)),
					selectedLaws.map((item) => String(item.id)),
					selectedProductTypes.map((item) => String(item.id))
				);
			} else {
				const itemIds = new Set(items.map((newItem) => newItem.id));
				const newThemes = selectedThemes.filter((selectedItem) => !itemIds.has(selectedItem.id));
				setSelectedThemesParam(newThemes.length > 0 ? newThemes : null);
				onChange(
					newThemes.map((item) => String(item.id)),
					selectedLaws.map((item) => String(item.id)),
					selectedProductTypes.map((item) => String(item.id))
				);
			}
		},
		value: selectedThemes,
		options: themeOptions || [],
		showMore: props.module.showAllThemesText,
		handleShowMore:
			totalThemes && totalThemes > maxItemsAmount
				? () => {
						dispatch(setModalTitle(props.module.themeFilterText));
						dispatch(setModalType('themes'));
						dispatch(setModalOpen(true));
				  }
				: undefined
	};

	const lawsFilter = {
		maxItemsAmount,
		id: 'laws',
		type: 'checkbox',
		title: props.module.lawFilterText,
		onChange: (items: CheckboxItem[], active: boolean): void => {
			if (active) {
				const mergedLaws = [...selectedLaws, ...items];
				setSelectedLawsParam(mergedLaws.length > 0 ? mergedLaws : null);
				onChange(
					selectedLaws.map((item) => String(item.id)),
					mergedLaws.map((item) => String(item.id)),
					selectedProductTypes.map((item) => String(item.id))
				);
			} else {
				const itemIds = new Set(items.map((newItem) => newItem.id));
				const newLaws = selectedLaws.filter((selectedItem) => !itemIds.has(selectedItem.id));
				setSelectedLawsParam(newLaws.length > 0 ? newLaws : null);
				onChange(
					selectedThemes.map((item) => String(item.id)),
					newLaws.map((item) => String(item.id)),
					selectedProductTypes.map((item) => String(item.id))
				);
			}
		},
		value: selectedLaws,
		options: lawOptions || [],
		showMore: props.module.showAllLawsText,
		handleShowMore:
			permitLawCount > maxItemsAmount
				? () => {
						dispatch(setModalTitle(props.module.lawFilterText));
						dispatch(setModalType('laws'));
						dispatch(setModalOpen(true));
				  }
				: undefined
	};

	const productTypeFilter = {
		maxItemsAmount,
		id: 'productTypes',
		type: 'checkbox',
		title: props.module.productTypeFilterText,
		onChange: (items: CheckboxItem[], active: boolean): void => {
			if (active) {
				const mergedProductTypes = [...selectedProductTypes, ...items];
				setSelectedProductTypesParam(mergedProductTypes.length > 0 ? mergedProductTypes : null);
				onChange(
					selectedLaws.map((item) => String(item.id)),
					selectedLaws.map((item) => String(item.id)),
					mergedProductTypes.map((item) => String(item.id))
				);
			} else {
				const itemIds = new Set(items.map((newItem) => newItem.id));
				const newProductTypes = selectedProductTypes.filter((selectedItem) => !itemIds.has(selectedItem.id));
				setSelectedProductTypesParam(newProductTypes.length > 0 ? newProductTypes : null);
				onChange(
					selectedThemes.map((item) => String(item.id)),
					selectedLaws.map((item) => String(item.id)),
					newProductTypes.map((item) => String(item.id))
				);
			}
		},
		value: selectedProductTypes,
		options: productTypeOptions || [],
		showMore: props.module.showAllProductTypesText,
		handleShowMore:
			permitProductTypeCount > maxItemsAmount
				? () => {
						dispatch(setModalTitle(props.module.productTypeFilterText));
						dispatch(setModalType('productTypes'));
						dispatch(setModalOpen(true));
				  }
				: undefined
	};

	return [themesFilter, lawsFilter, productTypeFilter] as IFilters;
};
